import { defineStore } from "pinia";
import { computed, reactive } from "vue";
import { useAuth } from "./auth";
import { settings, type TenantConfig } from "../api/medicus";
import { delegationContext } from "../utils/delegationContext";

export const useTenant = defineStore("tenant", () => {
  const authStore = useAuth();

  const JurisdictionType = {
    EnglandNHS: "england-nhs",
    IrelandHSE: "ireland-hse",
    Guernsey: "guernsey",
  } as const;

  // A delegated workflow reads and writes another workspace's records, so the organisation-type
  // flags below have to describe that workspace rather than the one the user logged into.
  // Fetched with the explicit per-call tenantId option (see utils/axios.ts) rather than letting
  // the ambient delegation context route it, so this never depends on which populator ran first.
  const delegateConfigurations = reactive<Record<string, TenantConfig>>({});
  const delegateConfigurationRequests: Record<string, Promise<TenantConfig>> = {};

  function loadDelegateConfiguration(tenantId: string) {
    const loaded = delegateConfigurations[tenantId];
    if (loaded) return Promise.resolve(loaded);

    if (!delegateConfigurationRequests[tenantId]) {
      delegateConfigurationRequests[tenantId] = settings({ tenantId, skipEtag: true })
        .then((configuration) => (delegateConfigurations[tenantId] = configuration))
        .finally(() => delete delegateConfigurationRequests[tenantId]);
    }

    return delegateConfigurationRequests[tenantId];
  }

  // Awaited by the router alongside the home workspace's settings, so a guard never decides a
  // route on organisation types that are still loading.
  function whenDelegateConfigurationLoaded() {
    const tenantId = delegationContext.targetTenantId;
    return tenantId ? loadDelegateConfiguration(tenantId) : Promise.resolve(null);
  }

  // Only the organisation types follow the delegate. Everything else on the configuration
  // describes the person and their session rather than the record they are looking at, so
  // demo, roles, staff, homepageUrl and jurisdiction all stay with the home workspace.
  const organisationTypes = computed(() => {
    const tenantId = delegationContext.targetTenantId;
    const configuration = tenantId ? delegateConfigurations[tenantId] : authStore.config;

    return configuration?.organisationTypes ?? [];
  });

  const getJurisdiction = computed(() => authStore.config?.jurisdiction);
  const isNHS = computed(() => authStore.config?.jurisdiction === JurisdictionType.EnglandNHS);
  const isGuernsey = computed(() => authStore.config?.jurisdiction === JurisdictionType.Guernsey);
  const isHSE = computed(() => authStore.config?.jurisdiction === JurisdictionType.IrelandHSE);
  const isGP = computed(() => organisationTypes.value.includes("gp-practice"));
  const isOOH = computed(
    () => authStore.config?.organisationTypes.includes("out-of-hours-service"),
  );
  const isSafeguardingService = computed(() =>
    organisationTypes.value.includes("safeguarding-service"),
  );
  const isMentalHealthProvider = computed(() => {
    return (
      organisationTypes.value.includes("mental-health-provider") ||
      organisationTypes.value.includes("community-care-service")
    );
  });
  const isCommunityServiceProvider = computed(() => {
    return organisationTypes.value.includes("community-service-provider");
  });
  const isHub = computed(() => {
    return (
      authStore.config?.organisationTypes.includes("primary-care-network") ||
      authStore.config?.organisationTypes.includes("gp-federation") ||
      authStore.config?.organisationTypes.includes("group-operator")
    );
  });
  const isDemo = computed(() => authStore.config?.demo);
  const isPfsApiEnabled = computed(() => authStore.config?.patientFacingServicesApiEnabled);
  const isMedicus = computed(() => authStore.config?.organisationTypes.includes("medicus"));
  const isOnboardingIncomplete = computed(() => authStore.config?.onboardingIncomplete ?? false);

  return {
    isNHS,
    isGuernsey,
    isHSE,
    isGP,
    isOOH,
    isMentalHealthProvider,
    isCommunityServiceProvider,
    isHub,
    isSafeguardingService,
    isDemo,
    isPfsApiEnabled,
    getJurisdiction,
    isMedicus,
    isOnboardingIncomplete,
    loadDelegateConfiguration,
    whenDelegateConfigurationLoaded,
  };
});
